import { createRouter, createWebHistory } from "vue-router";
import { Features, type RouterAuthService, type RouteRecordRawCustom } from "@/router/types.ts";
import type { Router, RouteComponent, RouteLocationNormalized, RouteLocationNormalizedLoaded } from "vue-router";
import { getAuthRouterGuard } from "./authRouterGuard";
import { useUiStore, useUserStore } from "@/store";
import { ROUTES, ROUTE_NAMES, TRACK_AND_TRACE_WORKSPACE_VIEWS } from "./constants";
import { CUSTOMER_FEATURE_FLAG_NAME, DEFAULT_QUERY } from "@/global";
import { userAnalyticsService } from "@/services";

const createFeatureGuard = (feature: Features | Features[]) => {
  return (__, _, next) => {
    // Check if auth bypass is enabled
    const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";
    if (bypassAuth) {
      return next(); // Skip feature check when using mock auth
    }

    const userStore = useUserStore();
    const { user, isFeatureEnabled } = userStore;

    // Support checking multiple features (any of them being enabled grants access)
    const features = Array.isArray(feature) ? feature : [feature];
    const hasFeatureAccess = features.some((f) => isFeatureEnabled(f));

    if (hasFeatureAccess && user?.preferences?.termsAccepted) {
      return next();
    }

    // Special handling for dashboard - redirect to first available feature
    if (features.includes(Features.DASHBOARD)) {
      const availableFeatures = [
        { feature: Features.SHIPMENTS, path: ROUTES.SHIPMENTS },
        { feature: Features.AIR_SHIPMENTS, path: ROUTES.AIR_SHIPMENTS },
        { feature: Features.BOOKINGS, path: ROUTES.BOOKINGS },
        { feature: [Features.DND, Features.DND_EXPORT], path: ROUTES.DND },
        { feature: Features.PORT_CONGESTION, path: ROUTES.PORT_CONGESTION },
        { feature: Features.EMISSIONS, path: ROUTES.EMISSIONS },
        { feature: Features.RISK_MANAGEMENT, path: ROUTES.RISK_MANAGEMENT },
        { feature: Features.CARRIER_PERFORMANCE, path: ROUTES.CARRIER_PERFORMANCE }
      ];

      for (const { feature: feat, path } of availableFeatures) {
        const feats = Array.isArray(feat) ? feat : [feat];
        if (feats.some((f) => isFeatureEnabled(f))) {
          router.push(path);
          return;
        }
      }
    }

    // If no features are enabled or non-dashboard feature is disabled
    router.push(ROUTES.UNAUTHORIZED);
  };
};

// True when the customer is on the Track & Trace experience, where the Shipments
// table is nested inside the Track & Trace page instead of living on its own route.
const isTrackAndTraceEnabled = (): boolean => {
  const { isCustomerFeatureFlagEnabled } = useUserStore();
  return isCustomerFeatureFlagEnabled(CUSTOMER_FEATURE_FLAG_NAME.ENABLE_TRACK_AND_TRACE_VIEW);
};

// Shipments needs page/limit/tab in the query; inject the defaults when they are missing.
// Shared by the standalone /shipments route and the one embedded in Track & Trace.
const createShipmentsGuard = () => {
  return (to, _, next) => {
    const userStore = useUserStore();
    const { user, isFeatureEnabled } = userStore;

    const requiredQueryParamsList = ["page", "limit", "tab"];

    const defaultQueriesExist = requiredQueryParamsList.every((queryItem) => Object.keys(to.query).includes(queryItem));

    if (isFeatureEnabled(Features.SHIPMENTS) && user?.preferences?.termsAccepted) {
      if (defaultQueriesExist) {
        return next();
      }

      return next({ ...to, query: { ...DEFAULT_QUERY, tab: "active" } });
    }
  };
};

// Metering access is gated by the env flag and required roles.
// Blocks direct URL access when the feature is disabled or roles/terms are not met.
const createMeteringGuard = () => {
  return (__, _, next) => {
    const bypassAuth = import.meta.env.VITE_BYPASS_AUTH === "true";
    if (bypassAuth) {
      return next();
    }

    const userStore = useUserStore();
    const { user, isFeatureEnabled } = userStore;

    if (isFeatureEnabled(Features.METERING) && user?.preferences?.termsAccepted) {
      return next();
    }

    router.push(ROUTES.UNAUTHORIZED);
  };
};

export const getRoutes = (): RouteRecordRawCustom[] => {
  return [
    {
      path: ROUTES.DASHBOARD,
      name: "Dashboard",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/ControlTowerView/ControlTowerView.vue"),
      props: true,
      beforeEnter: createFeatureGuard(Features.DASHBOARD)
    },
    {
      path: ROUTES.SHIPMENTS,
      name: ROUTE_NAMES.SHIPMENTS,
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/ShipmentsView/ShipmentsView.vue"),
      beforeEnter: (to, from, next) => {
        // Track & Trace customers see the Shipments table nested in /track-and-trace,
        // so legacy deep links (dashboard widgets, bookmarks, control tower) move there.
        if (isTrackAndTraceEnabled()) {
          return next({
            path: ROUTES.TRACK_AND_TRACE_SHIPMENTS,
            query: { ...DEFAULT_QUERY, tab: "active", ...to.query }
          });
        }

        return createShipmentsGuard()(to, from, next);
      }
    },
    {
      path: ROUTES.AIR_SHIPMENTS,
      name: "AirShipments",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/AirShipmentsView/AirShipmentsView.vue"),
      beforeEnter: createFeatureGuard(Features.AIR_SHIPMENTS)
    },
    {
      path: ROUTES.WORKSPACE + "/:workspaceId/:view",
      name: ROUTE_NAMES.WORKSPACE,
      meta: {
        requiresAuth: true
      },
      component: () => import("@/views/WorkspaceView/WorkspaceView.vue"),
      beforeEnter: (to, _, next) => {
        const userStore = useUserStore();
        const { user } = userStore;

        // Only the workspace *type* (the :view param) decides this - never the URL text,
        // since a workspace can be named "Shipments" too.
        if (TRACK_AND_TRACE_WORKSPACE_VIEWS.includes(String(to.params.view)) && isTrackAndTraceEnabled()) {
          return next({
            path: `${ROUTES.TRACK_AND_TRACE_WORKSPACE}/${encodeURIComponent(
              String(to.params.workspaceId)
            )}/${to.params.view}`,
            query: to.query
          });
        }

        if (user?.preferences?.termsAccepted) {
          return next();
        }
      }
    },
    {
      path: ROUTES.DND_LEGACY + "/:direction",
      name: "DnDImport",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/DnDView/DnDView.vue"),
      props: true,
      beforeEnter: createFeatureGuard([Features.DND, Features.DND_EXPORT])
    },
    {
      path: ROUTES.DND,
      name: "DnD",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> =>
        import("@/views/DemurrageDetentionView/DemurrageDetentionViewSwitch.vue"),
      props: true,
      beforeEnter: createFeatureGuard([Features.DND, Features.DND_EXPORT])
    },
    {
      path: ROUTES.DND_LEGACY + "/:direction/map-view",
      name: "DnDMapviewImport",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/DnDMapView/MapView.vue"),
      props: true,
      beforeEnter: createFeatureGuard([Features.DND, Features.DND_EXPORT])
    },
    {
      path: ROUTES.DND_LEGACY + "/:direction/map-view/shipments",
      name: "DnDMapShipmentImport",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/DnDMapView/PortContainersView.vue"),
      props: true,
      beforeEnter: createFeatureGuard([Features.DND, Features.DND_EXPORT])
    },
    {
      path: ROUTES.PORT_CONGESTION,
      name: "PortCongestion",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/PortCongestionView/PortCongestionView.vue"),
      props: true,
      beforeEnter: createFeatureGuard(Features.PORT_CONGESTION)
    },
    {
      path: ROUTES.PORT_CONGESTION_TERMINAL_VIEW,
      name: "PortCongestionTerminalView",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/PortCongestionView/PortCongestionView.vue"),
      props: true,
      beforeEnter: createFeatureGuard(Features.PORT_CONGESTION)
    },
    {
      path: ROUTES.EMISSIONS,
      name: "Emissions",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/EmissionsView/EmissionsView.vue"),
      props: true,
      beforeEnter: createFeatureGuard(Features.EMISSIONS)
    },
    {
      path: ROUTES.TERMS_AND_CONDITIONS,
      name: "TermsAndConditions",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/TermsAndConditionView/TermsAndConditionView.vue"),
      props: (route) => ({
        termsAccepted: route.query.termsAccepted,
        digitalMarketingAccepted: route.query.digitalMarketingAccepted
      })
    },
    {
      path: ROUTES.DATA_PROCESSING_AGREEMENTS,
      name: "DataProcessingAgreements",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/DataProcessingAgreements/DataProcessingAgreements.vue")
    },
    {
      path: ROUTES.UNAUTHORIZED,
      name: "Unauthorized",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/UnauthorizedView/UnauthorizedView.vue")
    },
    {
      path: ROUTES.RISK_MANAGEMENT + "/:tab?",
      name: "RiskManagement",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/RiskManagementView/RiskManagementViewRouter.vue"),
      beforeEnter: createFeatureGuard(Features.RISK_MANAGEMENT)
    },
    {
      path: ROUTES.REPORTING + "/:tab?/:id?",
      name: "Reporting",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/ReportingView/ReportingView.vue"),
      beforeEnter: createFeatureGuard(Features.REPORTING)
    },
    {
      path: ROUTES.UAM,
      name: "Uam",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/UamRemoteView/UamRemoteView.vue"),
      beforeEnter: createFeatureGuard(Features.UAM)
    },
    {
      path: ROUTES.INVOICE_AUDIT,
      name: "InvoiceAudit",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/InvoiceAuditRemoteView/InvoiceAuditRemoteView.vue"),
      beforeEnter: createFeatureGuard(Features.INVOICE_AUDIT)
    },
    {
      path: ROUTES.DND_NEW,
      name: "RedirectDndNew",
      redirect: ROUTES.DND
    },
    {
      path: ROUTES.CARRIER_PERFORMANCE,
      name: "RedirectCarrierPerformance",
      redirect: ROUTES.CARRIER_PERFORMANCE + "/departure"
    },
    {
      path: ROUTES.CARRIER_PERFORMANCE + "/:tab",
      name: "CarrierPerformance",
      meta: {
        requiresAuth: true
      },
      component: (): Promise<RouteComponent> => import("@/views/CarrierPerformanceView/CarrierPerformanceView.vue"),
      beforeEnter: createFeatureGuard(Features.CARRIER_PERFORMANCE)
    },
    {
      path: ROUTES.METERING,
      name: "Metering",
      meta: { requiresAuth: true },
      component: (): Promise<RouteComponent> => import("@/views/MeteringView/MeteringView.vue"),
      beforeEnter: createMeteringGuard()
    },
    {
      path: ROUTES.BOOKINGS,
      name: "Bookings",
      meta: { requiresAuth: true },
      component: (): Promise<RouteComponent> => import("@/views/BookingsView/BookingsView.vue"),
      beforeEnter: createFeatureGuard(Features.BOOKINGS)
    },
    {
      path: ROUTES.TRACK_AND_TRACE,
      name: ROUTE_NAMES.TRACK_AND_TRACE,
      meta: { requiresAuth: true },
      component: (): Promise<RouteComponent> => import("@/views/TrackAndTraceView/TrackAndTraceView.vue"),
      beforeEnter: createFeatureGuard(Features.TRACK_AND_TRACE),
      redirect: { name: ROUTE_NAMES.TRACK_AND_TRACE_SHIPMENTS },
      children: [
        {
          path: "shipments",
          name: ROUTE_NAMES.TRACK_AND_TRACE_SHIPMENTS,
          component: (): Promise<RouteComponent> => import("@/views/ShipmentsView/ShipmentsView.vue"),
          props: { embedded: true },
          beforeEnter: createShipmentsGuard()
        },
        {
          // Keeps the :view param of the standalone workspace route so WorkspaceView
          // resolves the same way; only shipments and orders workspaces belong under Track & Trace.
          path: "workspace/:workspaceId/:view",
          name: ROUTE_NAMES.TRACK_AND_TRACE_WORKSPACE,
          component: (): Promise<RouteComponent> => import("@/views/WorkspaceView/WorkspaceView.vue"),
          props: { embedded: true },
          beforeEnter: (to, _, next) => {
            if (!TRACK_AND_TRACE_WORKSPACE_VIEWS.includes(String(to.params.view))) {
              return next({
                path: `${ROUTES.WORKSPACE}/${encodeURIComponent(String(to.params.workspaceId))}/${to.params.view}`,
                query: to.query
              });
            }

            return next();
          }
        },
        {
          path: "orders",
          name: ROUTE_NAMES.TRACK_AND_TRACE_ORDERS,
          component: (): Promise<RouteComponent> => import("@/views/TrackAndTraceView/ordersView/OrdersView.vue")
        }
      ]
    },
    { path: "/:pathMatch(.*)*", redirect: "/" }
  ];
};

export const scrollBehavior = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalizedLoaded,
  savedPosition: any | null
) => {
  if (savedPosition) {
    return savedPosition;
  } else {
    return { left: 0, top: 0, behaviour: "smooth" };
  }
};

let router: Router;
let previousPath: string = "";

export const instantiateRouter = (authService: RouterAuthService): Router => {
  if (!router) {
    router = createRouter({
      history: createWebHistory("/"),
      scrollBehavior,
      routes: getRoutes()
    });

    router.beforeEach(getAuthRouterGuard(authService) as any);

    router.beforeEach(() => {
      const uiStore = useUiStore();
      uiStore.loading = true;
    });

    router.afterEach((to) => {
      const uiStore = useUiStore();
      uiStore.loading = false;

      // Only track pageviews manually when Plausible auto-tracking is disabled to avoid double-counting.
      if (!userAnalyticsService.isAutoTrackEnabled()) {
        const featureName = (to.name as string) || "unknown";
        const currentPath = to.path;

        userAnalyticsService.trackEvent("pageview", {
          path: currentPath,
          feature: featureName,
          referrer: previousPath || "direct"
        });
      }

      previousPath = to.path;
    });
  }
  return router;
};

export const getRouter = (): Router => router;
export const getCurrentPage = (): string => {
  return router.currentRoute?.value?.name as string;
};
